import { Controller } from "@hotwired/stimulus"
import { registerPageTools, toolResult } from "../lib/webmcp"
import { verifyWebmcpSession } from "../lib/webmcp_session"

export default class extends Controller {
  static targets = ["inloop"]
  static values = { url: String, query: String, team: String }

  connect() {
    this.stop()
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.registration = registerPageTools(this.tools(), {
      authorize: (signal) => verifyWebmcpSession(document.querySelector('meta[name="inloop-webmcp"]'), signal),
      isActive: () => this.element.isConnected && !document.documentElement.hasAttribute("data-turbo-preview")
    })
  }

  disconnect() {
    this.stop()
  }

  stop() {
    this.registration?.dispose()
    this.registration = null
  }

  tools() {
    return [
      {
        name: "inloop_list_inloops",
        description: "List inloops currently rendered on the signed-in facilitator page, including loaded collaborated inloops. Returns IDs, names, statuses, URLs, active filters, and whether more collaborated inloops can load by scrolling. This is not a complete account export.",
        inputSchema: { type: "object", properties: {}, additionalProperties: false },
        annotations: { readOnlyHint: true },
        execute: (input) => {
          this.validateInput(input, [])
          return toolResult({
            query: this.queryValue,
            teamId: this.teamValue,
            hasMoreShared: !!this.element.querySelector('[data-controller~="shared-inloops-autoload"]'),
            inloops: this.inloops()
          })
        }
      },
      {
        name: "inloop_search_inloops",
        description: "Search accessible inloops by title or description using the existing page search, keeping the selected team filter. Navigates to the results page; an empty query clears the search. Does not change any records.",
        inputSchema: {
          type: "object",
          properties: { query: { type: "string", description: "Title or description to search for, or an empty string to clear search.", maxLength: 200 } },
          required: ["query"],
          additionalProperties: false
        },
        execute: (input) => {
          this.validateInput(input, ["query"])
          if (typeof input.query !== "string" || input.query.length > 200) {
            throw new Error("query must be a string of at most 200 characters.")
          }
          const url = new URL(this.urlValue, window.location.origin)
          const query = input.query.trim()
          if (query) url.searchParams.set("q", query)
          if (this.teamValue !== "all") url.searchParams.set("team_id", this.teamValue)
          return this.navigate(url)
        }
      },
      {
        name: "inloop_open_inloop",
        description: "Open an inloop shown on this page using its ID from inloop_list_inloops. Navigates to its detail page without changing any records.",
        inputSchema: {
          type: "object",
          properties: { id: { type: "string", description: "The inloop ID returned by inloop_list_inloops." } },
          required: ["id"],
          additionalProperties: false
        },
        execute: (input) => {
          this.validateInput(input, ["id"])
          const inloop = typeof input.id === "string" && this.inloops().find((item) => item.id === input.id)
          if (!inloop) throw new Error("Choose an ID from the inloops currently listed on this page.")
          const url = new URL(inloop.url, window.location.origin)
          const index = new URL(this.urlValue, window.location.origin)
          if (url.pathname !== `${index.pathname}/${encodeURIComponent(inloop.id)}` || url.search || url.hash) {
            throw new Error("This inloop link is not a detail page.")
          }
          return this.navigate(url)
        }
      }
    ]
  }

  inloops() {
    return this.inloopTargets.map((link) => ({
      id: link.dataset.webmcpId,
      name: link.dataset.webmcpName,
      status: link.dataset.webmcpStatus,
      url: link.getAttribute("href")
    }))
  }

  validateInput(input, keys) {
    if (!input || typeof input !== "object" || Array.isArray(input) || Object.keys(input).some((key) => !keys.includes(key))) {
      throw new Error(`Expected an object with ${keys.length ? keys.join(", ") : "no arguments"}.`)
    }
  }

  navigate(url) {
    if (url.origin !== window.location.origin || url.username || url.password) {
      throw new Error("Only same-origin inloop navigation is supported.")
    }
    Turbo.visit(url.toString())
    return toolResult({ status: "navigating", url: url.pathname + url.search })
  }
}
